import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Img } from "@/components/common/Primitives";
import { SIZES } from "@/lib/images";
import { useLang } from "@/lib/i18n";
import { minPrice } from "@/lib/content";
import { nok } from "@/lib/format";

/**
 * Editorial room card: photo, caps label (house · guests), name, one line, hairline, price + link.
 * No pills, no boxes – the photo and the type carry it (design_guidelines.md).
 */
export const RoomCard = ({ rt, building, testid, eager = false, aspect = "aspect-[4/3]", index }) => {
  const { lang, tx, tr } = useLang();
  const id = testid || `room-card-${rt.id}`;
  const price = minPrice(rt);
  const label = [building ? tr(building.name) : null, tx(`Inntil ${rt.max_guests} ${rt.max_guests === 1 ? "gjest" : "gjester"}`, `Up to ${rt.max_guests} ${rt.max_guests === 1 ? "guest" : "guests"}`)].filter(Boolean).join(" · ");
  return (
    <Link to={`/rom/${rt.slug}`} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid={id}>
      <Img src={rt.images?.[0]} alt={tr(rt.name)} className={`img-zoom ${aspect}`} eager={eager} sizes={SIZES.third} />
      <div className="mt-5">
        <p className="index-num flex items-center gap-3 text-muted-foreground">
          {index !== undefined && <><span className="tnum">{String(index).padStart(2, "0")}</span><span className="h-px w-6 bg-border" aria-hidden /></>}
          <span className="label-caps">{label}</span>
        </p>
        <h3 className="mt-3 text-[1.375rem] font-medium leading-[1.15] tracking-[-0.02em] text-foreground sm:text-[1.5rem]">{tr(rt.name)}</h3>
        <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(rt.tagline)}</p>
        <div className="mt-5 flex items-baseline justify-between gap-4 border-t border-border pt-4 text-sm">
          {price ? (
            <p className="text-muted-foreground tabular">{tx("Frå", "From")} <span className="font-medium text-foreground" data-testid={`${id}-price`}>{nok(price, lang)}</span> {tx("per natt", "per night")}</p>
          ) : <span />}
          <span className="inline-flex shrink-0 items-center gap-1.5 font-medium text-foreground">
            <span className="link-grow">{tx("Sjå rommet", "See the room")}</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </Link>
  );
};
